import { createSelector } from 'reselect';
import ReviewsConstants from 'Shared/constants/Reviews.constants';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';

// TODO (josh) : this key should come from a constant
const getReviewItems = state => state.reviewItems;

function createParameterSelector(selector) {
  return (_, params) => selector(params);
}

const getReviewSlug = createParameterSelector(params => params.reviewSlug);
const getReviewSlugs = createParameterSelector(params => params.reviewSlugs);
const getCompilerSlug = createParameterSelector(params => params.compilerSlug);
const getFormType = createParameterSelector(params => params.formType);
const getFormSlug = createParameterSelector(params => params.formSlug);
const getStatus = createParameterSelector(params => params.status);
const getUserSlug = createParameterSelector(params => params.userSlug);
const getReviewCycleSlug = createParameterSelector(params => params.reviewCycleSlug);
const getReviewTemplateType = createParameterSelector(params => params.reviewTemplateType);

export const getAllReviews = createSelector([getReviewItems], reviews => {
  return reviews.get(ReviewsConstants.State.REVIEWS_BY_REVIEW_SLUG);
});

export const getAllReviewForms = createSelector([getReviewItems], reviews => {
  return reviews.get(ReviewsConstants.State.REVIEW_FORMS_BY_REVIEW_FORM_SLUG);
});

export const getReviewsByReviewTemplateType = createSelector(
  [getAllReviews, getReviewTemplateType],
  (reviews, templateType = null) => {
    if (templateType) {
      return reviews.filter(review => review.get('reviewTemplateType') === templateType);
    }
    return reviews;
  }
);

export const getReviewBySlug = createSelector([getAllReviews, getReviewSlug], (reviews, slug) => {
  return reviews.get(slug, null);
});

export const getReviewsBySlugs = createSelector([getAllReviews, getReviewSlugs], (reviews, slugs) => {
  return reviews.filter(review => slugs.includes(ReviewsHelpers.getSlug(review)));
});

export const getReviewsByStatus = createSelector([getReviewsByReviewTemplateType, getStatus], (reviews, status) => {
  if (status) {
    return reviews.filter(review => status.includes(ReviewsHelpers.getState(review)));
  } else return reviews;
});

export const getReviewsByCompilerSlug = createSelector(
  [getReviewsByStatus, getCompilerSlug],
  (reviews, compilerSlug) => {
    if (compilerSlug) {
      return reviews.filter(review => review.get('compilerSlug') === compilerSlug);
    }
    return reviews;
  }
);

export const getReviewByReviewCycleSlug = createSelector(
  [getAllReviews, getReviewCycleSlug],
  (reviews, reviewCycleSlug) => {
    return reviews.filter(review => review.get('reviewCycleSlug') === reviewCycleSlug);
  }
);

export const getReviewFormBySlug = createSelector([getAllReviewForms, getFormSlug], (reviewForms, formSlug) => {
  return reviewForms.get(formSlug);
});

export const getReviewFormsByReviewSlugs = createSelector(
  [getAllReviewForms, getReviewSlugs],
  (reviewForms, slugs = []) => {
    if (slugs.length > 0) {
      return reviewForms.filter(reviewForm => slugs.includes(ReviewFormHelpers.getReviewSlug(reviewForm)));
    }
    return reviewForms;
  }
);

export const getReviewFormsByFormType = createSelector(
  [getReviewFormsByReviewSlugs, getFormType],
  (reviewForms, formType) => {
    return reviewForms.filter(reviewForm => reviewForm.get('formType') === formType);
  }
);

export const getReviewFormsByReviewSlug = createSelector(
  [getAllReviewForms, getReviewSlug],
  (reviewForms, reviewSlug) => {
    return reviewForms.filter(reviewForm => reviewForm.get('reviewSlug') === reviewSlug);
  }
);

export const getReviewFormsByReviewSlugAndType = createSelector(
  [getReviewFormsByReviewSlug, getFormType],
  (reviewForms, formType) => {
    return reviewForms.filter(reviewForm => reviewForm.get('formType') === formType);
  }
);

export const getReviewFormsByReviewSlugAndUserSlug = createSelector(
  [getReviewFormsByReviewSlug, getUserSlug],
  (reviewForms, userSlug) => {
    return reviewForms.find(reviewForm => ReviewFormHelpers.getUserSlug(reviewForm) === userSlug);
  }
);

export const getReviewFormsByReviewSlugAndUserSlugAndFormType = createSelector(
  [getReviewFormsByReviewSlugAndType, getUserSlug],
  (reviewForms, userSlug) => {
    return reviewForms.find(reviewForm => ReviewFormHelpers.getUserSlug(reviewForm) === userSlug);
  }
);

export const getReviewFormsByUserSlugAndType = createSelector(
  [getReviewFormsByFormType, getUserSlug],
  (reviewForms, userSlug) => {
    return reviewForms.filter(reviewForm => ReviewFormHelpers.getUserSlug(reviewForm) === userSlug);
  }
);
